import { useEffect, useRef, useState } from "react";

import { useRouteMatch } from "react-router-dom";

import { setFavorites } from "@/api/endpoints/favorite";
import { postTrackingData } from "@/api/endpoints/track";
import DotsHorizontal from "@/assets/icons/dots-horizontal.svg";
import FavoriteIcon from "@/assets/icons/favorite.svg";
import {
  INSTAGRAM_TITLE,
  MentionType,
  NON_AUDIO_MEDIA,
  SOURCE,
  Source,
} from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { useTags } from "@/contexts/TagContext";
import useHash from "@/hooks/useHash";
import { ShareIcon } from "@/Icons";
import { EVENT } from "@/tracking_events";
import { cx, isSourceWithTitle } from "@/utils";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";
import ExpandDescription from "@components/ExpandDescription";
import TextButton from "@components/TextButton";
import Typography from "@components/Typography";

import SourceDisplay from "../../../SourceDisplay";
import { useMentionCard } from "../../context";
import { getShowName } from "../../utils/getShowName";
import Cover from "../Cover";
import AudioBodyMobile from "./AudioBodyMobile";
import TextBodyMobile from "./TextBodyMobile";

import styles from "./MentionCardMobile.module.css";

const getBodyComponent = (
  source: Source,
  context: MentionType,
  title?: string
) => {
  // text posts ("meta" for SoMe) are always text
  if (context === "text_post") {
    return TextBodyMobile;
  }

  // instagram gets different components if video or text
  if (source === SOURCE.INSTAGRAM) {
    return title === INSTAGRAM_TITLE.VIDEO ? AudioBodyMobile : TextBodyMobile;
  }

  // non-audio media gets text
  if (NON_AUDIO_MEDIA.includes(source)) {
    return TextBodyMobile;
  }

  // rest gets audio
  return AudioBodyMobile;
};

type MentionCardMobileProps = {
  className?: string;
};

const MentionCardMobile: React.FC<MentionCardMobileProps> = ({ className }) => {
  const mentionCard = useMentionCard();
  const {
    channel: {
      channel_type,
      external_source,
      website_url,
      name,
      artwork_url: channelCover,
    },
    episode: {
      title,
      artwork_url: episodeCover,
      external_media_source: episodeUrl,
      guid: episodeGuid,
      published_date,
      audio_url,
      episode_type,
    },
    share_url,
    mentions,
    hideActions,
    episodeDescriptionMentions,
    episodeNameMentions,
    is_paywalled,
    is_favorited,
  } = mentionCard;

  const source =
    channel_type === SOURCE.YOUTUBE && episode_type === "shorts"
      ? SOURCE.YOUTUBE_SHORTS
      : channel_type;

  const cover = episodeCover || channelCover;

  const { shareV4 } = useMentionCards();
  const { formatDate } = useI18n();
  const { fetchOrganizationTags } = useTags() || {
    organizationTags: [],
  };
  // expand mentions if public view
  const defaultOpen = useRouteMatch("/(sv|en)/m/:slug--:uuid");

  // If a hash is present in the URL, focuses the matching mention
  const mentionCardRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);
  const hash = useHash();
  useEffect(() => {
    const mention_uuids: Array<string | null> =
      mentions?.map((m) => m.id) || [];
    // adding meta-hits to uuids-array, if any.
    const titleMentionUuid = episodeNameMentions[0]?.id;
    const descriptionMentionUuid = episodeDescriptionMentions[0]?.id;
    mention_uuids.push(titleMentionUuid, descriptionMentionUuid);

    if (mention_uuids.includes(hash) && mentionCardRef.current) {
      // Offset scrolling by a timeout to allow the page to render first.
      // Does not work in chrome without it.
      setTimeout(() => {
        mentionCardRef.current &&
          mentionCardRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
      }, 50);

      setHighlight(true);

      const t = setTimeout(() => {
        setHighlight(false);
      }, 3500);

      return () => clearTimeout(t);
    }
  }, [hash, episodeNameMentions, episodeDescriptionMentions, mentions]);

  const Body =
    mentions.length && getBodyComponent(source, mentions[0].context, title);

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isFavorited, setIsFavorited] = useState(is_favorited);

  const onMarkFavorite = () => {
    if (!isFavorited) {
      postTrackingData(EVENT.MARK_FAVOURITE, {
        url: share_url,
        source: audio_url,
      });
    }

    setIsFavorited((v) => !v);

    setFavorites(
      mentions.map((m) => m.id),
      !isFavorited
    )
      .then(() => {
        fetchOrganizationTags();
      })
      .catch((error) => {
        throw error;
      });
  };

  return (
    <div
      ref={mentionCardRef}
      className={cx(styles.root, { [styles.highlight]: highlight }, className)}
      data-testid="mobile-episode-container"
    >
      <div className={styles.header}>
        <Cover
          source={source}
          cover={cover}
          episodeUrl={episodeUrl}
          episodeGuid={episodeGuid}
        />

        <div className={styles.info}>
          <div className={styles.row}>
            <div className={styles.timestamp}>
              <SourceDisplay
                title={title}
                variant="body-2"
                className={styles.source}
                noIcon
                source={source}
                external_source={external_source}
              />
              {", "}
              {formatDate(published_date, "PPP")}
            </div>
          </div>

          <Typography
            className={cx(styles.title, "highlight-match")}
            block
            data-testid="show-title"
            variant="subtitle-1"
            {...(website_url
              ? {
                  component: "a",
                  href: website_url,
                  target: "_blank",
                  rel: "noreferrer",
                }
              : {})}
          >
            {getShowName({ channel_type: source, name })}
            {isSourceWithTitle(source) && (
              <>
                {": "}
                <span dangerouslySetInnerHTML={{ __html: title }} />
              </>
            )}
          </Typography>
        </div>
        {!hideActions && (
          <div className={styles.actions}>
            <Dropdown>
              <DropdownToggle>
                <TextButton
                  className="unstyle-dropdown"
                  data-testid="mention-action-menu-toggler"
                  icon={DotsHorizontal}
                />
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem
                  className={cx(styles.item, { favorited: isFavorited })}
                  data-testid="mark-favorite-button-mobile"
                  onClick={onMarkFavorite}
                >
                  <FavoriteIcon
                    className={cx(styles.icon, {
                      [styles.favorited]: isFavorited,
                    })}
                  />{" "}
                  {L.MENTIONS_MARK_AS_FAVORITE}
                </DropdownItem>
                <DropdownItem
                  className={styles.item}
                  data-testid="share-button-mobile"
                  onClick={() => shareV4(mentionCard)}
                >
                  <ShareIcon className={styles.icon} /> {L.MENTIONS_SHARE}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>

      {episodeDescriptionMentions.length > 0 &&
        episodeDescriptionMentions[0].display_text &&
        episodeDescriptionMentions[0].trimmed_display_text && (
          <div className={styles.description}>
            <Typography
              data-testid="episode-description"
              className="highlight-match"
              component="span"
              variant="body-2"
              dangerouslySetInnerHTML={{
                __html: showFullDescription
                  ? episodeDescriptionMentions[0].display_text
                  : episodeDescriptionMentions[0].trimmed_display_text,
              }}
            />

            {episodeDescriptionMentions[0].display_text.length >
              episodeDescriptionMentions[0].trimmed_display_text.length && (
              <ExpandDescription
                showFullDescription={showFullDescription}
                onClick={() => setShowFullDescription(!showFullDescription)}
              />
            )}
          </div>
        )}

      {is_paywalled && (
        <span data-testid="episode-paywalled">
          {L.MENTIONS_CONTEXT_PAYWALLED}
        </span>
      )}
      {Body ? <Body defaultOpen={Boolean(defaultOpen)} /> : null}
      <hr />
    </div>
  );
};

export default MentionCardMobile;
